import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE_URL } from '../config/api';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import UniversitySignupForm from '../components/forms/UniversitySignupForm';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const SignupNew = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const { showSuccess, showError } = useToast();
 const isUniversitySignup = searchParams.get('partner') === 'university';

 useDocumentMetadata(
   isUniversitySignup ? 'University Registration — Dakhla' : 'Student Sign Up — Dakhla',
   isUniversitySignup
     ? 'Register your institution on Dakhla to reach thousands of prospective applicants and manage admissions.'
     : 'Create a free student profile on Dakhla to search, compare, and apply to top universities across Pakistan.'
 );

 const [formData, setFormData] = useState({
 full_name: '',
 email: '',
 gender: '',
 phone_number: '',
 date_of_birth: '',
 password: '',
 confirmPassword: ''
 });

 const [showPassword, setShowPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 const [loading, setLoading] = useState(false);
 const [errors, setErrors] = useState({});
 const [genders, setGenders] = useState([]);
 const [verificationPending, setVerificationPending] = useState(false);
 const [pendingEmail, setPendingEmail] = useState('');

 useEffect(() => {
 document.title = isUniversitySignup ? 'University Signup - Dakhla' : 'Student Signup - Dakhla';
 }, [isUniversitySignup]);

 useEffect(() => {
 fetchGenders();
 }, []);

 const fetchGenders = async () => {
 try {
 const response = await fetch(`${API_BASE_URL}/lookup/genders`);
 if (response.ok) {
 const data = await response.json();
 setGenders(data.genders || []);
 }
 } catch (error) {
 console.error('Error fetching genders:', error);
 }
 };

 const handleChange = (e) => {
 const { name, value } = e.target;
 setFormData(prev => ({
 ...prev,
 [name]: value
 }));
 if (errors[name]) {
 setErrors(prev => ({ ...prev, [name]: '' }));
 }
 };

 const validateForm = () => {
 const newErrors = {};

 if (!formData.full_name.trim()) {
 newErrors.full_name = 'Full name is required';
 }

 if (!formData.email.trim()) {
 newErrors.email = 'Email is required';
 } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
 newErrors.email = 'Please enter a valid email';
 }

 if (!formData.password) {
 newErrors.password = 'Password is required';
 } else if (formData.password.length < 8) {
 newErrors.password = 'Password must be at least 8 characters';
 }

 setErrors(newErrors);
 return Object.keys(newErrors).length === 0;
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!validateForm()) return;
 setLoading(true);

 try {
 const response = await fetch(`${API_BASE_URL}/student/signup`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 fullName: formData.full_name,
 email: formData.email,
 password: formData.password
 }),
 });

 const data = await response.json();

 if (response.ok) {
 setPendingEmail(formData.email);
 setVerificationPending(true);
 } else {
 showError(data.error || 'Signup failed');
 }
 } catch (error) {
 console.error('Signup error:', error);
 showError('Network error. Please try again.');
 } finally {
 setLoading(false);
 }
 };

 const handleUniversitySubmit = async (formData) => {
 setLoading(true);

 try {
 const response = await fetch(`${API_BASE_URL}/university/signup`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(formData),
 });

 const data = await response.json();

 if (response.ok) {
 setPendingEmail(formData.email || data.email || '');
 setVerificationPending(true);
 } else {
 showError(data.error || 'Signup failed');
 }
 } catch (error) {
 console.error('University signup error:', error);
 showError('Network error. Please try again.');
 } finally {
 setLoading(false);
 }
 };

 // Shared input class
 const inputClass = (fieldName) =>
 `appearance-none block w-full px-4 py-2.5 bg-white border rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm ${
 errors[fieldName] ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
 }`;

 // University signup view
 if (isUniversitySignup) {
 return (
 <div className="min-h-screen bg-slate-50 relative flex flex-col justify-center py-12 sm:px-6 lg:px-8 overflow-y-auto">
 <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
 <div className="absolute top-0 left-0 w-full h-full opacity-[0.025] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:24px_24px]" />
 <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary-500/8 blur-3xl animate-pulse-ring" style={{ animationDuration: '9s' }} />
 <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-indigo-500/8 blur-3xl animate-pulse-ring" style={{ animationDuration: '12s' }} />
 </div>

 <div className="sm:mx-auto sm:w-full sm:max-w-2xl relative z-10 px-4 sm:px-0">
 <div className="flex flex-col items-center mb-6">
 <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">
 Register your university
 </h1>
 <p className="text-sm text-slate-500 mt-1">
 Manage applications and connect with students on Dakhla
 </p>
 </div>

 <div className="card relative overflow-hidden bg-white py-7 px-5 sm:rounded-2xl sm:px-8 border border-slate-200 shadow-lg shadow-slate-100">

 <UniversitySignupForm onSubmit={handleUniversitySubmit} loading={loading} />

 <div className="mt-5 pt-4 border-t border-slate-100 text-center">
 <span className="text-sm text-slate-500">
 Want to sign up as a student?{' '}
 <Link to="/signup" className="font-semibold text-primary-600 hover:text-primary-500 transition-colors">
 Student signup
 </Link>
 </span>
 </div>
 </div>
 </div>
 </div>
 );
 }

 // Email verification pending screen
 if (verificationPending) {
 return (
 <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-16">
 <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10 text-center">
 <div className="h-16 w-16 rounded-2xl bg-primary-50 border border-primary-200 flex items-center justify-center mx-auto mb-5">
 <svg className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
 <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
 </svg>
 </div>
 <h1 className="text-xl font-extrabold text-slate-800 mb-2">Check your inbox</h1>
 <p className="text-sm text-slate-500 mb-6">
 We've sent a verification link to{' '}
 <strong className="text-slate-700">{pendingEmail}</strong>.<br />
 Click the link in the email to activate your account.
 </p>
 <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500 text-left mb-6">
 <p className="font-semibold text-slate-600 mb-1">Didn't receive the email?</p>
 <ul className="list-disc list-inside space-y-1">
 <li>Check your spam or junk folder.</li>
 <li>The link expires in <strong>24 hours</strong>.</li>
 </ul>
 </div>
 <Link to="/login" className="text-sm font-semibold text-primary-600 hover:underline">
 Back to Sign In
 </Link>
 </div>
 </div>
 );
 }

 // Student signup view
 return (
 <div className="bg-slate-50 relative flex items-center justify-center py-8 sm:px-6 lg:px-8" style={{ minHeight: 'calc(100vh - var(--navbar-height, 64px))' }}>
 {/* Background decoration */}
 <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
 <div className="absolute top-0 left-0 w-full h-full opacity-[0.025] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:24px_24px]" />
 <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary-500/8 blur-3xl animate-pulse-ring" style={{ animationDuration: '9s' }} />
 <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-indigo-500/8 blur-3xl animate-pulse-ring" style={{ animationDuration: '12s' }} />
 </div>

 <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10 px-4 sm:px-0">
 {/* Header */}
 <div className="flex flex-col items-center mb-6 text-center">
 <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">
 Start your Dakhla journey
 </h1>
 <p className="text-xs text-slate-400 mt-2">
 Already have an account?{' '}
 <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-500 transition-colors">
 Sign in
 </Link>
 </p>
 </div>

 {/* Card */}
 <div className="card relative overflow-hidden bg-white py-9 px-5 sm:rounded-2xl sm:px-10 border border-slate-200 shadow-lg shadow-slate-100">
 {/* Accent line */}

 <form className="space-y-4" onSubmit={handleSubmit}>

 {/* Full Name */}
 <div>
 <label htmlFor="full_name" className="block text-xs font-semibold text-slate-700 mb-1">
 Full Name <span className="text-red-500">*</span>
 </label>
 <input
 id="full_name"
 name="full_name"
 type="text"
 value={formData.full_name}
 onChange={handleChange}
 className={inputClass('full_name')}
 placeholder="Enter your full name"
 />
 {errors.full_name && (
 <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.full_name}</p>
 )}
 </div>

 {/* Email */}
 <div>
 <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">
 Email <span className="text-red-500">*</span>
 </label>
 <input
 id="email"
 name="email"
 type="email"
 value={formData.email}
 onChange={handleChange}
 className={inputClass('email')}
 placeholder="you@example.com"
 />
 {errors.email && (
 <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.email}</p>
 )}
 </div>

 {/* Password */}
 <div>
 <label htmlFor="password" className="block text-xs font-semibold text-slate-700 mb-1">
 Password <span className="text-red-500">*</span>
 </label>
 <div className="relative">
 <input
 id="password"
 name="password"
 type={showPassword ? 'text' : 'password'}
 value={formData.password}
 onChange={handleChange}
 className={`${inputClass('password')} pr-11`}
 placeholder="Min. 8 characters"
 />
 <button
 type="button"
 onClick={() => setShowPassword(!showPassword)}
 className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
 >
 {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
 </button>
 </div>
 {errors.password && (
 <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.password}</p>
 )}
 </div>

 {/* Submit */}
 <div className="pt-1">
 <button
 id="signup-submit"
 type="submit"
 disabled={loading}
 className={`w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-xl text-sm font-semibold text-white shadow-md active:scale-[0.98] transition-premium cursor-pointer ${
 loading
 ? 'bg-slate-400 cursor-not-allowed shadow-none'
 : 'bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-700 hover:to-indigo-700 shadow-primary-500/20 hover:shadow-lg hover:shadow-primary-500/25 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
 }`}
 >
 {loading ? (
 <>
 <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
 </svg>
 <span>Creating Account...</span>
 </>
 ) : (
 'Create Account'
 )}
 </button>
 </div>

 {/* Fine print */}
 <p className="text-center text-xs text-slate-400 font-medium pt-1">
 Complete your student profile after registration and start applying to our partnered universities.
 </p>
 </form>
 </div>
 </div>
 </div>
 );
};

export default SignupNew;